import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useForm } from "@tanstack/react-form";
import { PrimaryButton } from "@/components/buttons/PrimaryButton";
import PencomTextField from "@/components/input/PencomTextField";
import PencomDropdown from "@/components/input/PencomDropdown";
import { Tooltip } from "@/components/common/Tooltip";
import { ContactUpdateSuccessDialog } from "@/components/onboarding/ContactUpdateSuccessDialog";
import { PencomRoutes } from "@/router";
import { toast } from "@/utils/toast";
import { maskEmail, maskPhone } from "@/utils/maskUtils";
import {
  useValidateCorrectionLink,
  useSubmitContactUpdate,
} from "@/hooks/correction-link/correction-link.hook";
import {
  CONTACT_UPDATE_DESIGNATION_OPTIONS,
  EmployerContactUpdateSchema,
} from "@/forms/employer-contact-update/employer-contact-update.schema";
import { type EmployerContactUpdateFormValues } from "@/forms/employer-contact-update/employer-contact-update.form";

/** Extracts a fieldName -> message map (plus a "form" root message) from a Zod SafeParse failure. */
function toFieldErrorMap(
  result: ReturnType<typeof EmployerContactUpdateSchema.safeParse>
): Record<string, string> | undefined {
  if (result.success) return undefined;
  const errors: Record<string, string> = {};
  for (const issue of result.error.issues) {
    const key = issue.path.length ? String(issue.path[0]) : "form";
    if (!errors[key]) errors[key] = issue.message;
  }
  return errors;
}

const ContactUpdatePage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");

  const {
    data: validationData,
    isPending: isValidating,
    isError: isValidationError,
  } = useValidateCorrectionLink(token);

  const submitContactUpdateMutation = useSubmitContactUpdate(token);
  const [isSuccessDialogOpen, setIsSuccessDialogOpen] = useState(false);
  const [updatedContactEmail, setUpdatedContactEmail] = useState("");

  const form = useForm({
    defaultValues: {
      name: "",
      email: "",
      designation: "",
    } as EmployerContactUpdateFormValues,
    validators: {
      onSubmit: ({ value }) => {
        const result = EmployerContactUpdateSchema.safeParse(value);
        return toFieldErrorMap(result);
      },
    },
    onSubmit: async ({ value }) => {
      submitContactUpdateMutation.mutate(value, {
        onSuccess: (response) => {
          setUpdatedContactEmail(response.contactPersonEmail || value.email);
          setIsSuccessDialogOpen(true);
        },
        onError: (error) => {
          toast.error(
            error.message ||
              "Unable to update your contact person. Please try again."
          );
        },
      });
    },
  });

  const handleContinueToLogin = () => {
    setIsSuccessDialogOpen(false);
    navigate(PencomRoutes.LOGIN, { replace: true });
  };

  if (!token) {
    return (
      <div className="flex flex-col items-center text-center gap-y-3 py-16">
        <h2 className="font-clash-display font-semibold text-xl text-medium-dark-grey">
          Invalid link
        </h2>
        <p className="font-clash-display text-sm text-medium-grey max-w-[420px]">
          This link is missing required information. Please use the link
          from your email, or request a new one from the login screen.
        </p>
      </div>
    );
  }

  if (isValidating) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4" />
          <p className="font-clash-display text-medium-grey">
            Validating your link...
          </p>
        </div>
      </div>
    );
  }

  if (isValidationError || validationData?.isValid === false) {
    const message = isValidationError
      ? "Unable to validate this link. Please try again or request a new one."
      : validationData?.reason === "EXPIRED"
      ? "This link has expired. Please request a new one from the login screen."
      : validationData?.reason === "USED"
      ? "This link has already been used. Please request a new one if you still need to update your contact person."
      : "This link is invalid. Please request a new one from the login screen.";

    return (
      <div className="flex flex-col items-center text-center gap-y-3 py-16">
        <h2 className="font-clash-display font-semibold text-xl text-medium-dark-grey">
          Link not available
        </h2>
        <p className="font-clash-display text-sm text-medium-grey max-w-[420px]">
          {message}
        </p>
      </div>
    );
  }

  return (
    <div className="w-full flex flex-col max-w-[620px]">
      <div className="flex flex-col gap-y-4 mb-6">
        <h1 className="text-[40px] font-semibold text-center font-clash-display text-medium-dark-grey tracking-[-0.6px] leading-tight">
          Update Contact Person
        </h1>
        <p className="font-clash-display font-normal text-md text-center text-medium-dark-grey tracking-[-0.08px]">
          Confirm your company details below and enter the new contact
          person who should receive login OTPs going forward.
        </p>
      </div>

      <div className="flex flex-col gap-y-6 mb-6">
        <div className="bg-white border border-stroke rounded-lg p-4 w-full">
          <div className="flex gap-4 items-center pb-3 border-b border-stroke">
            <div className="bg-pale-mint-green rounded-full size-8 flex items-center justify-center shrink-0">
              <img src="/assets/icons/building.svg" alt="" className="w-6 h-6" />
            </div>
            <p className="font-clash-display font-semibold text-xl text-medium-dark-grey">
              {validationData?.currentName || "—"}
            </p>
          </div>
          <div className="grid grid-cols-2 gap-x-8 gap-y-4 pt-4">
            <div className="flex flex-col gap-1">
              <p className="font-clash-display text-sm text-medium-dark-grey">
                RC Number
              </p>
              <p className="font-clash-display font-semibold text-base leading-6 text-medium-dark-grey">
                {validationData?.currentRcNumber || "—"}
              </p>
            </div>
            <div className="flex flex-col gap-1">
              <p className="font-clash-display text-sm text-medium-dark-grey">
                Employer Code
              </p>
              <p className="font-clash-display font-semibold text-base leading-6 text-medium-dark-grey">
                {validationData?.employerCode || "—"}
              </p>
            </div>
            <div className="flex flex-col gap-1">
              <p className="font-clash-display text-sm text-medium-dark-grey">
                Company Name
              </p>
              <p className="font-clash-display font-semibold text-base leading-6 text-medium-dark-grey">
                {validationData?.currentName || "—"}
              </p>
            </div>
            <div className="flex flex-col gap-1">
              <p className="font-clash-display text-sm text-medium-dark-grey">
                Address
              </p>
              <p className="font-clash-display font-semibold text-base leading-6 text-medium-dark-grey">
                {validationData?.currentAddress || "—"}
              </p>
            </div>
            <div className="flex flex-col gap-1">
              <p className="font-clash-display text-sm text-medium-dark-grey">
                Contact Email
              </p>
              <p className="font-clash-display font-semibold text-base leading-6 text-medium-dark-grey">
                {maskEmail(validationData?.currentEmail ?? "") || "—"}
              </p>
            </div>
            <div className="flex flex-col gap-1">
              <p className="font-clash-display text-sm text-medium-dark-grey">
                Contact Phone Number
              </p>
              <p className="font-clash-display font-semibold text-base leading-6 text-medium-dark-grey">
                {maskPhone(validationData?.currentContactPersonPhone ?? "") ||
                  "—"}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="flex flex-col gap-y-6">
        <form.Subscribe
          selector={(state) => state.errorMap.onSubmit as Record<string, string> | undefined}
          children={(onSubmitErrors) => (
            <>
              <form.Field
                name="name"
                children={(field) => (
                  <PencomTextField
                    id="new-contact-person-name"
                    label="New Contact Person Name"
                    type="text"
                    placeholder="Enter contact person's full name"
                    value={field.state.value}
                    onChange={(e) => field.handleChange(e.target.value)}
                    error={!!onSubmitErrors?.name}
                    helperText={onSubmitErrors?.name}
                  />
                )}
              />

              <form.Field
                name="email"
                children={(field) => (
                  <div className="flex flex-col gap-2">
                    <div className="flex items-center gap-2">
                      <p className="font-clash-display text-medium-dark-grey text-md font-semibold">
                        New Contact Person Email
                      </p>
                      <Tooltip content="This is the email address that will be used to receive login OTPs">
                        <img
                          src="/assets/icons/info-outline.svg"
                          alt="info"
                          className="w-4 h-4"
                          aria-hidden
                        />
                      </Tooltip>
                    </div>
                    <PencomTextField
                      id="new-contact-person-email"
                      label=""
                      type="email"
                      placeholder="Enter contact person's email address"
                      value={field.state.value}
                      onChange={(e) => field.handleChange(e.target.value)}
                      error={!!onSubmitErrors?.email}
                      helperText={onSubmitErrors?.email}
                    />
                  </div>
                )}
              />

              <form.Field
                name="designation"
                children={(field) => (
                  <PencomDropdown
                    id="new-contact-person-designation"
                    label="Contact Person Designation"
                    placeholder="Select designation"
                    options={CONTACT_UPDATE_DESIGNATION_OPTIONS.map((role) => ({
                      id: role,
                      label: role,
                    }))}
                    value={field.state.value}
                    onChange={(e) => field.handleChange(e.target.value)}
                    helperText={onSubmitErrors?.designation}
                  />
                )}
              />
            </>
          )}
        />

        <PrimaryButton
          isLoading={submitContactUpdateMutation.isPending}
          disabled={submitContactUpdateMutation.isPending}
          className="mt-2 w-full"
          title="Update Contact Person"
          onClick={() => form.handleSubmit()}
        />
      </div>

      <ContactUpdateSuccessDialog
        open={isSuccessDialogOpen}
        onOpenChange={(open) => {
          setIsSuccessDialogOpen(open);
          if (!open) handleContinueToLogin();
        }}
        newContactEmail={updatedContactEmail}
        onContinue={handleContinueToLogin}
      />
    </div>
  );
};

export default ContactUpdatePage;
